import { FormikProps, setNestedObjectValues } from 'formik';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useRef } from 'react';

// Fields show their error only once touched, so a loaded entity that already fails
// validation (e.g. rules tightened after it was saved) keeps the save button disabled
// with nothing on screen to fix. Touch the invalid fields as soon as the values load.
// loadedValues is what the form was given as initialValues once loaded, undefined until then.
export const useTouchErrorsOnLoad = (
  formik: Pick<
    FormikProps<any>,
    'initialValues' | 'validateForm' | 'setTouched'
  >,
  loadedValues: unknown,
) => {
  const { initialValues, validateForm, setTouched } = formik;
  const handledValuesRef = useRef<unknown>();

  useEffect(() => {
    // formik.initialValues is reinitialized in Formik's own effect, a render after the prop
    // changes; validating before it catches up would check (and touch) the previous values.
    if (!loadedValues || !isEqual(initialValues, loadedValues)) {
      return;
    }
    // Formik reinitializes (and resets touched) only when initialValues deeply change; a new
    // object with the same contents must not overwrite what the user has touched since load.
    if (isEqual(handledValuesRef.current, initialValues)) {
      return;
    }
    handledValuesRef.current = initialValues;

    validateForm(initialValues).then((errors) => {
      // Drop the result of a validation that started for values since replaced.
      if (handledValuesRef.current === initialValues && !isEmpty(errors)) {
        setTouched(setNestedObjectValues(errors, true), false);
      }
    });
  }, [loadedValues, initialValues, validateForm, setTouched]);
};
